import React, { useCallback, useState } from 'react';
import {
  calculateLevelingWindow,
  getDropTuningValuesVM,
  histogramSmoothing,
  saveDropTuningValues,
} from 'components/commandpanel/ImageTuning/ImageTuningManager';
import { useRMDispatch } from 'redux/hooks';
import { updateTuningValues } from 'redux/reducers/wellReducer';
import { GetCaptureProfileTuningValues, base64ToImage } from 'utils/Global';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import ImageTuning from './imageTuning';
import { defaultLowerLevelLimit, defaultUpperLevelLimit } from '../constants';
import { useWellDropImageData } from 'redux/hooks/well';
import { useSelectedWellDropDetails } from 'redux/hooks/searchnFilter';

const ImageTuningManager = () => {
  const dispatch = useRMDispatch();
  const [reset, setReset] = useState<boolean | undefined>(undefined);
  const [isAuto, setIsAuto] = useState<boolean | undefined>(undefined);
  const wellDropDetails = useSelectedWellDropDetails();
  const { plateID, wellNum, dropNumber, imageBatchId, cpID, regionID } =
    wellDropDetails?.[0] || {};
  const imageData = useWellDropImageData(
    plateID,
    wellNum,
    dropNumber,
    imageBatchId,
    cpID,
  );

  const { lowerLevelLimit, upperLevelLimit, isAutoLevelingOn } =
    imageData?.image?.captureResultTuningValuesDto || {};

  const setTuningValues = useCallback(
    async (params: number[], autoLevelingOn?: boolean) => {
      dispatch(
        updateTuningValues({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber,
          imageBatchId,
          cpId: cpID,
          lowerLimit: params[0],
          upperLimit: params[1],
          autoLevelingOn: autoLevelingOn ?? false,
        }),
      );
    },
    [dispatch, wellDropDetails],
  );

  const saveTuningValues = useCallback(
    async (params: number[], autoLevelingOn?: boolean) => {
      const lowerLimit = params[0];
      const upperLimit = params[1];
      saveDropTuningValues(
        lowerLimit,
        upperLimit,
        cpID,
        regionID,
        imageBatchId,
        autoLevelingOn ?? false,
      );
    },
    [cpID, regionID, imageBatchId, saveDropTuningValues],
  );

  const GetHistogramFromCompositeImage = (imgEl: HTMLImageElement) => {
    if (imgEl == undefined) {
      return undefined;
    }
    const defaultRGB = {
      r: 0,
      g: 0,
      b: 0,
    }; // for non-supporting envs

    let data,
      i = -4,
      avgColor = 0; //ARGB

    const rgb = {
      a: 0,
      r: 0,
      g: 0,
      b: 0,
    };
    const canvas = document.createElement('canvas');
    const context = canvas.getContext && canvas.getContext('2d');
    const bytesPerPixel = 4;
    const histogram = new Array(256).fill(0);
    if (!context) {
      return defaultRGB;
    }
    const height = (canvas.height =
      imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height);
    const width = (canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width);
    context.drawImage(imgEl, 0, 0);

    try {
      data = context.getImageData(0, 0, width, height);
    } catch (e) {
      console.error(e);
      return defaultRGB;
    }
    const length = data.data.length;
    while ((i += bytesPerPixel) < length) {
      rgb.r = data.data[i];
      rgb.g = data.data[i + 1];
      rgb.b = data.data[i + 2];
      rgb.a = data.data[i + 3];
      if (rgb.a > 0) {
        avgColor = ~~((rgb.r + rgb.g + rgb.b) / 3); //~~ is for floor function
        histogram[avgColor]++;
      }
    }
    return histogram;
  };

  const getAutoTuningWindowForDrop = (
    orgImage: HTMLImageElement,
    captureResultTuningValuesVM: ImageTuningValues,
  ) => {
    let histogramValues;
    const imageTuningVM: ImageTuningValues = {};
    if (captureResultTuningValuesVM) {
      if (captureResultTuningValuesVM.isAutoLevelingOn) {
        histogramValues = GetHistogramFromCompositeImage(orgImage);
        histogramValues = histogramSmoothing(histogramValues);
        if (histogramValues != null) {
          const window = calculateLevelingWindow(
            histogramValues,
            captureResultTuningValuesVM.autoLevelingLowThreshold,
            captureResultTuningValuesVM.autoLevelingHighThreshold,
          );
          if (window != undefined) {
            imageTuningVM.autoLevelingLowerLimit = window[0];
            imageTuningVM.autoLevelingUpperLimit = window[1];
            imageTuningVM.lowerLevelLimit = window[0];
            imageTuningVM.upperLevelLimit = window[1];
            imageTuningVM.isAutoLevelingOn = captureResultTuningValuesVM.isAutoLevelingOn;
          }
        }
      } else {
        imageTuningVM.lowerLevelLimit = captureResultTuningValuesVM.lowerLevelLimit;
        imageTuningVM.upperLevelLimit = captureResultTuningValuesVM.upperLevelLimit;
      }
    }
    return imageTuningVM;
  };

  const onAutoClick = async () => {
    const tuningValues = getDropTuningValuesVM(
      lowerLevelLimit,
      upperLevelLimit,
      cpID,
      regionID,
      !isAutoLevelingOn,
    );
    if (imageData?.image?.wellImage) {
      if (!isAuto) {
        setIsAuto(true);
        const img = await base64ToImage(imageData?.image?.wellImage);
        const tuningWindow = getAutoTuningWindowForDrop(img, tuningValues);
        setTuningValues(
          [tuningWindow?.lowerLevelLimit, tuningWindow?.upperLevelLimit],
          true,
        );
        saveTuningValues(
          [tuningWindow?.lowerLevelLimit, tuningWindow?.upperLevelLimit],
          true,
        );
      } else {
        setIsAuto(false);
        setTuningValues(
          [tuningValues?.lowerLevelLimit, tuningValues?.upperLevelLimit],
          false,
        );
        saveTuningValues(
          [tuningValues?.lowerLevelLimit, tuningValues?.upperLevelLimit],
          false,
        );
      }
    }
  };

  const onResetClick = () => {
    setReset(true);
    const captureProfileTuningValues = GetCaptureProfileTuningValues();
    const cptuningValues = captureProfileTuningValues.find(
      (tv) => tv.captureProfileID == cpID,
    );
    const tuningValues = getDropTuningValuesVM(
      cptuningValues?.lowerLevelLimit ?? defaultLowerLevelLimit,
      cptuningValues?.upperLevelLimit ?? defaultUpperLevelLimit,
      cpID,
      regionID,
      false,
    );
    if (reset) {
      setTuningValues([defaultLowerLevelLimit, defaultUpperLevelLimit]);
      saveTuningValues([defaultLowerLevelLimit, defaultUpperLevelLimit], false);
    } else {
      setTuningValues([tuningValues?.lowerLevelLimit, tuningValues?.upperLevelLimit]);
      saveTuningValues(
        [tuningValues?.lowerLevelLimit, tuningValues?.upperLevelLimit],
        false,
      );
    }
  };

  const onMinValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      const max = upperLevelLimit;
      if (value < max && value >= defaultLowerLevelLimit) {
        setTuningValues([value, max]);
      }
    },
    [upperLevelLimit, defaultLowerLevelLimit, setTuningValues],
  );

  const onMaxValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      const min = lowerLevelLimit;
      if (value > min && value <= defaultUpperLevelLimit) {
        setTuningValues([min, value]);
      }
    },
    [lowerLevelLimit, defaultUpperLevelLimit, setTuningValues],
  );

  return (
    <ImageTuning
      lowerLevelLimit={lowerLevelLimit}
      upperLevelLimit={upperLevelLimit}
      isAutoLevelingOn={isAutoLevelingOn}
      wellImage={imageData?.image?.wellImage}
      setTuningValues={setTuningValues}
      saveTuningValues={saveTuningValues}
      onMinValueChange={onMinValueChange}
      onMaxValueChange={onMaxValueChange}
      onAutoClick={onAutoClick}
      onResetClick={onResetClick}
    />
  );
};

export default ImageTuningManager;
